// Centralized number formatting for KPI tiles and tables.
// Full values keep thousands separators; compact values ("€1.02M") are for
// tiles where the full figure would not fit — pair them with a tooltip.

type Num = number | null | undefined;

const EUR = '€';

/** 80282 → "80,282". Empty string for null/NaN so callers can fall back to "—". */
export const formatNumber = (value: Num, decimals = 0): string => {
  if (value == null || Number.isNaN(value)) return '';
  return value.toLocaleString('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

/** 80282 → "€80,282" */
export const formatMoney = (value: Num, currency = EUR): string => {
  if (value == null || Number.isNaN(value)) return '';
  const sign = value < 0 ? '−' : '';
  return `${sign}${currency}${formatNumber(Math.abs(value))}`;
};

/** 3533140000 → "3.53B", 1018234 → "1.02M", 80282 → "80.3k", 950 → "950" */
export const formatCompact = (value: Num): string => {
  if (value == null || Number.isNaN(value)) return '';
  const abs = Math.abs(value);
  const sign = value < 0 ? '−' : '';
  if (abs >= 1_000_000_000) return `${sign}${trim(abs / 1_000_000_000, 2)}B`;
  if (abs >= 1_000_000) return `${sign}${trim(abs / 1_000_000, 2)}M`;
  if (abs >= 10_000) return `${sign}${trim(abs / 1_000, 1)}k`;
  return `${sign}${formatNumber(abs)}`;
};

/** 1018234 → "€1.02M" */
export const formatMoneyCompact = (value: Num, currency = EUR): string => {
  const compact = formatCompact(value);
  if (!compact) return '';
  return compact.startsWith('−') ? `−${currency}${compact.slice(1)}` : `${currency}${compact}`;
};

/** 0.1234 → "12.3%" */
export const formatPercent = (ratio: Num, decimals = 1): string => {
  if (ratio == null || Number.isNaN(ratio)) return '';
  return `${trim(ratio * 100, decimals)}%`;
};

const trim = (n: number, decimals: number) => String(Number(n.toFixed(decimals)));
